/**
 * Read-path blob schemas (frontend-architecture, ADR-0016): the CDN artifacts are parsed with
 * these runtime schemas on the client and validated with the same schemas by the blob generator,
 * so one schema guards both ends of the CDN seam. Shapes are pinned in database.md (Blob storage
 * structure). Parsing is deliberately lenient about unknown keys — an open client can meet an
 * artifact from a newer deploy — but required fields are enforced, so a generator that drops one
 * is caught at write time.
 *
 * The five artifact families: the polled `manifest.json`, `config/public.json`, `teams/teams.json`,
 * `contests/{id}/contest.json`, and the per-event `contests/{id}/events/{eventId}.json`. The event
 * blob carries the live overlay fields (`activeProps`, `voidedProps`, `scores`) from its first
 * write — reserved here so later slices (CAE-259/260/264) fill them without reshaping the artifact.
 */
import { z } from 'zod';
import { DEFAULT_MARKET_TYPE_TAB_ORDER } from './market-type-order.js';
/** A per-event lifecycle status; the UI renders `resolved` as "Final" (database.md). */
export const eventStatusSchema = z.enum(['scheduled', 'live', 'resolved']);
/** A Contest's lifecycle state (contests.state; database.md). A blob exists only once finalised. */
export const contestStateSchema = z.enum(['Scheduled', 'Open', 'Closed', 'Settled', 'Canceled']);
/** An over/under side; the settlement match resolves through the chosen Outcome (database.md). */
export const directionSchema = z.enum(['over', 'under']);
/** An artifact version: a bare number, or an event's `{ v, status }` (database.md). */
const artifactVersionSchema = z.union([
    z.number(),
    z.object({ v: z.number(), status: eventStatusSchema }),
]);
/**
 * `manifest.json`: the active contest id, the next contest's opens-at (or null), and a version
 * per artifact. Polled `no-cache`; a blob is refetched only when its version moves (database.md,
 * read-path-consume). `activeContestId` is null when no contest is live.
 */
export const manifestSchema = z.object({
    activeContestId: z.string().nullable(),
    nextContestOpensAt: z.string().nullable(),
    versions: z.record(z.string(), artifactVersionSchema),
});
/**
 * The `navigate` payload the parent sportsbook routes on. It is stored as public configuration
 * (the feed's `competition_id` is env-specific, prefilled from the catalogue allowlist; api.md,
 * decided 2026-07-07) and the frontend forwards it **verbatim** into `buildNavigate`
 * (@cou/iframe-protocol) — so the keys are the snake_case postMessage shape, not camelCase.
 */
export const navigatePayloadSchema = z.object({
    sport: z.string(),
    competition_name: z.string(),
    competition_id: z.string(),
});
/**
 * `config/public.json`: the write-time whitelist of `is_public` configuration (ADR-0005). Carries
 * the maintenance flag, the (reserved) launch gate, the `navigate` routing payload, the display
 * copy overrides, and the Play-tab market-type order (CAE-523). `launchGate` is null until a gate
 * is configured and an open object when set — its inner shape is deferred, reserved here so a gate
 * lands without a schema break (view-active-contest). `marketTypeTabOrder` defaults so an older
 * blob without the key still parses on a newer client.
 */
export const publicConfigSchema = z.object({
    maintenanceMode: z.boolean(),
    launchGate: z.record(z.string(), z.unknown()).nullable(),
    navigate: navigatePayloadSchema,
    copy: z.object({
        noContest: z.string(),
        contestLocked: z.string(),
        serviceUnavailable: z.string(),
    }),
    marketTypeTabOrder: z.array(z.string()).default([...DEFAULT_MARKET_TYPE_TAB_ORDER]),
});
/**
 * One team's display row: name and abbreviation for the chip, brand hex, and a logo URL. Colours
 * and `logoUrl` are nullable — the DB columns are (a team may lack brand hex, and logos are null
 * until Caesars delivers them, rendering the initials/colour fallback; ADR-0011, database.md).
 */
export const teamDisplaySchema = z.object({
    name: z.string(),
    abbreviation: z.string(),
    primary: z.string().nullable(),
    secondary: z.string().nullable(),
    logoUrl: z.string().nullable(),
});
/**
 * `teams/teams.json`: the team registry keyed by team id, so the client resolves a slate row's
 * team to its display data in O(1) (decided 2026-07-13). Refreshed in place, not snapshotted.
 */
export const teamsBlobSchema = z.object({
    teams: z.record(z.string(), teamDisplaySchema),
});
/** An event-index entry in `contest.json`: the near-static fields the event-filter chips need. */
export const contestEventIndexEntrySchema = z.object({
    id: z.string(),
    startTime: z.string(),
    home: z.object({ abbr: z.string(), logo: z.string().nullable() }),
    away: z.object({ abbr: z.string(), logo: z.string().nullable() }),
});
/**
 * `contests/{id}/contest.json`: contest meta, the most-picked `popular` ordering (recomputed
 * while Open; backfilled from the curated slate when pick volume is low), and the event index. Carries **no reward or segment data** (ADR-0015); the
 * flipping per-event `status` lives in the manifest, so this artifact stays near-static after
 * finalisation (database.md, read-path-blob-generation).
 */
export const contestBlobSchema = z.object({
    contestId: z.string(),
    name: z.string(),
    state: contestStateSchema,
    opensAt: z.string(),
    closesAt: z.string(),
    popular: z.array(z.string()),
    events: z.array(contestEventIndexEntrySchema),
});
/** A team as rendered on the per-event board: display name, abbreviation, logo, and one brand hex. */
export const eventTeamSchema = z.object({
    name: z.string(),
    abbr: z.string(),
    logo: z.string().nullable(),
    color: z.string().nullable(),
});
/** The player a prop is about; `headshot` is null until roster enrichment ships (ADR-0011). */
export const athleteSchema = z.object({
    name: z.string(),
    headshot: z.string().nullable(),
    teamAbbr: z.string(),
});
/** One selectable side of a Market: its Selection id and direction (odds never enter a blob). */
export const outcomeSchema = z.object({
    id: z.string(),
    direction: directionSchema,
});
/** One player prop on the frozen slate: the normalised type, the frozen Line, athlete, and sides. */
export const marketSchema = z.object({
    id: z.string(),
    marketType: z.string(),
    line: z.number(),
    athlete: athleteSchema,
    outcomes: z.array(outcomeSchema),
});
/**
 * One in-play score: a per-market live value only (never per-pick or per-user — the client derives
 * X-of-N against the frozen Line). Provisional and display-only; settlement reads the DB (database.md).
 */
export const liveScoreSchema = z.object({
    marketId: z.string(),
    liveValue: z.number(),
});
/**
 * `contests/{id}/events/{eventId}.json`: one Event's frozen slate (Line and structure, **no odds**)
 * plus the live overlay. The three overlay fields are present from the first write — empty until
 * filled — so later slices extend the artifact rather than reshape it (decided 2026-07-13):
 * - `activeProps`: currently selectable market ids (odds within margin, not started; CAE-259)
 * - `voidedProps`: market ids voided in settlement staging; drives the void badge (CAE-260, GL-09)
 * - `scores`: per-market in-play values (CAE-264)
 */
export const eventBlobSchema = z.object({
    id: z.string(),
    name: z.string(),
    startTime: z.string(),
    home: eventTeamSchema,
    away: eventTeamSchema,
    activeProps: z.array(z.string()),
    voidedProps: z.array(z.string()),
    markets: z.array(marketSchema),
    scores: z.array(liveScoreSchema),
});
//# sourceMappingURL=blobs.js.map